
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Avatar,
    AvatarGroup,
    InputAdornment,
    TablePagination,
    useTheme,
    alpha,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Skeleton
} from '@mui/material';
import {
    Sync as SyncIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Image as ImageIcon,
    Close as CloseIcon,
    Search as SearchIcon,
    MoreVert as MoreVertIcon,
    Key as KeyIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import { newsService } from '../service/newsService';
import { getImageUrl } from '../service/api';
import SuccessNotification from '../components/SuccessNotification';

export default function News() {
    const theme = useTheme();

    const [news, setNews] = useState([]);
    const [open, setOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedNewsId, setSelectedNewsId] = useState(null);
    const [selectedImage, setSelectedImage] = useState('');
    const [newsAnchorEl, setNewsAnchorEl] = useState(null);
    const [selectedNews, setSelectedNews] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewData, setViewData] = useState(null);

    const [formData, setFormData] = useState({
        longUserAccessToken: ''
    });

    // Pagination & Search state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    // Notification State
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchNews();
    }, []);

    const fetchNews = async () => {
        setLoading(true);
        try {
            const response = await newsService.getNews(1, 100);
            if (response.success && response.data) {
                setNews(response.data || []);
            }
        } catch (error) {
            console.error("Error fetching news", error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpen = () => {
        setOpen(true);
        setFormData({
            longUserAccessToken: ''
        });
    };

    const handleClose = () => {
        setOpen(false);
        setFormData({
            longUserAccessToken: ''
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!formData.longUserAccessToken.trim()) {
            setNotification({ open: true, message: 'Access Token is required', severity: 'error' });
            return;
        }

        setLoading(true);
        try {
            const response = await newsService.createNews({
                longUserAccessToken: formData.longUserAccessToken
            });

            if (response.success) {
                fetchNews();
                handleClose();
                setNotification({ open: true, message: `News synced successfully from Facebook!`, severity: 'success' });
            } else {
                setNotification({ open: true, message: response.message || "Sync failed", severity: 'error' });
            }
        } catch (error) {
            console.error("Error syncing news", error);
            setNotification({ open: true, message: "Error syncing news", severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSyncAction = async () => {
        setLoading(true);
        try {
            const response = await newsService.syncNews();
            if (response.success) {
                fetchNews();
                setNotification({ open: true, message: `News synced successfully!`, severity: 'success' });
            } else {
                setNotification({ open: true, message: response.message || "Sync failed", severity: 'error' });
            }
        } catch (error) {
            console.error("Error syncing news", error);
            setNotification({ open: true, message: "Error syncing news", severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedNewsId(id);
        setDeleteModalOpen(true);
        handleNewsMenuClose();
    };

    const handleDeleteConfirm = async () => {
        if (!selectedNewsId) return;
        setLoading(true);
        try {
            await newsService.deleteNews(selectedNewsId);
            fetchNews();
            setDeleteModalOpen(false);
            setSelectedNewsId(null);
            setNotification({ open: true, message: 'News deleted successfully!', severity: 'success' });
        } catch (error) {
            console.error("Error deleting news", error);
        } finally {
            setLoading(false);
        }
    };

    const handleImagePreview = (imageUrl) => {
        setSelectedImage(imageUrl);
        setImagePreviewOpen(true);
    };

    const handleNewsMenuOpen = (event, newsItem) => {
        setNewsAnchorEl(event.currentTarget);
        setSelectedNews(newsItem);
    };

    const handleNewsMenuClose = () => {
        setNewsAnchorEl(null);
    };

    const handleViewClick = (item) => {
        setViewData(item);
        setViewModalOpen(true);
    };

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: { xs: 3, sm: 4 },
                p: { xs: 2, sm: 3 },
                gap: 2,
                borderRadius: 4,
                bgcolor: 'white',
                boxShadow: '0 2px 12px rgba(0,0,0,0.03)'
            }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: '-0.5px', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Facebook News Sync</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        startIcon={<KeyIcon />}
                        onClick={handleOpen}
                        sx={{
                            borderRadius: 2.5,
                            px: 3,
                            py: 1.2,
                            width: { xs: '100%', sm: 'auto' },
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.95rem'
                        }}
                    >
                        Set Token
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SyncIcon />}
                        onClick={handleSyncAction}
                        disabled={loading}
                        sx={{
                            borderRadius: 2.5,
                            px: 3,
                            py: 1.2,
                            width: { xs: '100%', sm: 'auto' },
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            background: 'primary.main',
                            '&:hover': {
                                background: 'primary.dark',
                            }
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Sync News'}
                    </Button>
                </Box>
            </Box>

            <Paper sx={{
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 10px 30px 0 rgba(0,0,0,0.04)',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                mb: 4
            }}>
                <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: '1px solid', borderColor: '#f1f5f9', bgcolor: '#ffffff' }}>
                    <TextField
                        fullWidth
                        placeholder="Search local news feed..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(0);
                        }}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                            sx: {
                                bgcolor: '#f8fafc',
                                borderRadius: 3,
                                '& fieldset': { border: 'none' },
                                '&:hover': { bgcolor: '#f1f5f9' },
                                transition: 'all 0.2s'
                            }
                        }}
                    />
                </Box>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 800 }}>
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, px: 3 }}>Actions</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Message</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && news.length === 0 ? (
                                Array.from(new Array(5)).map((_, index) => (
                                    <TableRow key={index}>
                                        <TableCell sx={{ px: 3 }}><Skeleton variant="circular" width={32} height={32} /></TableCell>
                                        <TableCell><Skeleton variant="rectangular" width={40} height={40} /></TableCell>
                                        <TableCell><Skeleton width="80%" /></TableCell>
                                    </TableRow>
                                ))
                            ) : news
                                .filter(item =>
                                    item.message?.toLowerCase().includes(searchTerm.toLowerCase())
                                )
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((item) => (
                                    <TableRow key={item.id} hover>
                                        <TableCell sx={{ px: 3 }}>
                                            <IconButton size="small" onClick={(e) => handleNewsMenuOpen(e, item)}>
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            {item.imageUrl ? (
                                                <Avatar
                                                    variant="rounded"
                                                    src={getImageUrl(item.imageUrl)}
                                                    alt="Post Image"
                                                    sx={{ width: 56, height: 56, cursor: 'pointer' }}
                                                    onClick={() => handleImagePreview(getImageUrl(item.imageUrl))}
                                                />
                                            ) : (
                                                <Avatar variant="rounded" sx={{ width: 56, height: 56, bgcolor: '#f1f5f9', color: '#94a3b8' }}><ImageIcon /></Avatar>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {item.message || 'No description'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            {!loading && news.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">No news synced yet. Click "Sync from Facebook" to start.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={news.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, p) => setPage(p)}
                        onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                    />
                </TableContainer>
            </Paper>

            {/* Menu for Actions */}
            <Menu
                anchorEl={newsAnchorEl}
                open={Boolean(newsAnchorEl)}
                onClose={handleNewsMenuClose}
            >
                <MenuItem onClick={() => {
                    handleViewClick(selectedNews);
                    handleNewsMenuClose();
                }}>
                    <ListItemIcon><VisibilityIcon fontSize="small" color="primary" /></ListItemIcon>
                    <ListItemText primary="View Details" />
                </MenuItem>
                <MenuItem onClick={() => handleDeleteClick(selectedNews?.id)} sx={{ color: 'error.main' }}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText primary="Delete Entry" />
                </MenuItem>
            </Menu>

            {/* Sync Modal */}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Sync News from Facebook</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                        Please provide your Facebook Long-lived Access Token to fetch the latest news feed into your dashboard.
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <TextField
                            label="Long-lived Access Token"
                            name="longUserAccessToken"
                            fullWidth
                            multiline
                            rows={3}
                            value={formData.longUserAccessToken}
                            onChange={handleChange}
                            required
                            placeholder="Enter your FB Access Token..."
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <KeyIcon color="primary" sx={{ opacity: 0.6 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleClose} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={loading} startIcon={<SyncIcon />}>
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Start Sync'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>Are you sure you want to delete this news entry?</DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={imagePreviewOpen} onClose={() => setImagePreviewOpen(false)} maxWidth="md">
                <Box sx={{ position: 'relative' }}>
                    <IconButton
                        onClick={() => setImagePreviewOpen(false)}
                        sx={{ position: 'absolute', right: 8, top: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box component="img" src={selectedImage} sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
                </Box>
            </Dialog>

            <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    News Details
                    <IconButton onClick={() => setViewModalOpen(false)} size="small" sx={{ color: 'text.secondary' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {viewData && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {viewData.imageUrl ? (
                                <Box
                                    component="img"
                                    src={getImageUrl(viewData.imageUrl)}
                                    alt="News"
                                    sx={{ width: '100%', borderRadius: 2, maxHeight: 300, objectFit: 'contain', bgcolor: '#f8fafc' }}
                                />
                            ) : (
                                <Box sx={{ width: '100%', height: 200, borderRadius: 2, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <ImageIcon sx={{ fontSize: 64, color: '#cbd5e1' }} />
                                </Box>
                            )}
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: '#334155' }}>
                                {viewData.message || 'No description available.'}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Posted: {viewData.createdTime ? new Date(viewData.createdTime).toLocaleString() : 'N/A'}
                                </Typography>
                                {viewData.permalinkUrl && (
                                    <Button size="small" variant="outlined" href={viewData.permalinkUrl} target="_blank" rel="noopener">
                                        Open in Facebook
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <SuccessNotification
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                handleClose={() => setNotification({ ...notification, open: false })}
            />
        </Box>
    );
}
